import React, { useEffect, useState } from 'react';
import clinicService from '../../services/clinicService';
import userService from '../../services/userService';
import { clinicRoleSelectValue } from '../../utils/clinicRole';
import { useT } from '../../i18n/LanguageContext';

export const unwrap = (res) => {
  const data = res.data?.data?.data || res.data?.data || [];
  return Array.isArray(data) ? data : [];
};

export function RepAssignments({ clinics, onSaved }) {
  const t = useT();
  const [reps, setReps] = useState([]);
  const [selectedRep, setSelectedRep] = useState('');
  const [checked, setChecked] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    userService.getUsers({ role: 'sales_rep' }).then((res) => {
      const all = unwrap(res);
      setReps(all.filter((u) => u.role === 'sales_rep'));
    }).catch(() => setReps([]));
  }, []);

  useEffect(() => {
    const rep = reps.find((r) => r._id === selectedRep);
    setChecked((rep?.clinics || []).map((c) => (c?._id || c).toString()));
  }, [selectedRep, reps]);

  const toggle = (id) => {
    setChecked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const save = async () => {
    setSaving(true);
    try {
      await clinicService.assignRepClinics(selectedRep, checked);
      // Reflect the save locally so re-selecting the rep shows truth without a refetch.
      setReps((prev) => prev.map((r) => (r._id === selectedRep ? { ...r, clinics: checked } : r)));
      onSaved();
    } catch (err) {
      alert(t('Failed to save assignments'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-[#e2e8f0] rounded-[14px] shadow-sm p-5 mb-6">
      <p className="text-[14px] font-semibold text-[#0f172a] mb-1">{t('Rep Assignments')}</p>
      <p className="text-[12px] text-[#64748b] mb-3">
        {t('Pick a sales rep, check the clinics they cover, save. Reps only see and submit for their assigned clinics.')}
      </p>
      <select
        value={selectedRep}
        onChange={(e) => setSelectedRep(e.target.value)}
        aria-label={t('Sales rep')}
        className="w-full max-w-[320px] h-[40px] border border-[#d6dce8] rounded-[8px] px-2 text-[13px] text-[#0f172a] bg-white mb-3"
      >
        <option value="">{t('Select a sales rep...')}</option>
        {reps.map((r) => (
          <option key={r._id} value={r._id}>
            {`${r.firstName || ''} ${r.lastName || ''}`.trim() || r.email}
          </option>
        ))}
      </select>
      {selectedRep && (
        <>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-2 mb-3">
            {clinics.map((c) => (
              <label key={c._id} className="flex items-center gap-2 text-[13px] text-[#0f172a]">
                <input
                  type="checkbox"
                  checked={checked.includes(c._id)}
                  onChange={() => toggle(c._id)}
                />
                {c.name}
              </label>
            ))}
            {clinics.length === 0 && (
              <p className="text-[12px] text-[#64748b] col-span-full">
                {t('No clinics yet - run Sync from QuickBooks first.')}
              </p>
            )}
          </div>
          <button
            onClick={save}
            disabled={saving}
            className="h-[36px] px-5 bg-[#0089ff] text-white text-[13px] font-medium rounded-[6px] hover:bg-[#0077e6] transition-colors disabled:opacity-50"
          >
            {saving ? t('Saving...') : t('Save Assignments')}
          </button>
        </>
      )}
    </div>
  );
}

/**
 * Inline per-clinic rate editor. The $/unit rate the clinic pays is maintained
 * in-app (QB has no per-customer rate API). A blank rate is legitimate and DELIBERATELY sends that
 * clinic's invoices to needs_attention rather than billing a guess - so an empty box saves as null.
 */
export function RateCell({ clinic, onRateSaved }) {
  const t = useT();
  const [value, setValue] = useState(clinic.ratePerUnit ?? '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setValue(clinic.ratePerUnit ?? '');
  }, [clinic.ratePerUnit]);

  const dirty = String(value) !== String(clinic.ratePerUnit ?? '');

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const trimmed = String(value).trim();
      const rate = trimmed === '' ? null : Number(trimmed);
      const res = await clinicService.setRate(clinic._id, rate);
      const applied = res.data?.data?.ratePerUnit ?? res.data?.ratePerUnit ?? rate;
      onRateSaved(clinic._id, applied);
      setSaved(true);
    } catch (err) {
      alert(t('Failed to save rate'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[11px] text-[#97a3b6]">$</span>
      <input
        type="number"
        min="0"
        step="0.01"
        value={value}
        onChange={(e) => { setValue(e.target.value); setSaved(false); }}
        aria-label={t('Rate for {name}', { name: clinic.name })}
        placeholder="—"
        className="w-[70px] h-[30px] border border-[#d6dce8] rounded-[6px] px-2 text-[12px] text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff]"
      />
      {dirty && (
        <button
          onClick={save}
          disabled={saving}
          className="h-[30px] px-2 bg-[#0089ff] text-white text-[11px] font-medium rounded-[6px] hover:bg-[#0077e6] disabled:opacity-50"
        >
          {saving ? '...' : t('Save')}
        </button>
      )}
      {saved && !dirty && <span className="material-symbols-outlined text-[16px] text-[#007a55]">check</span>}
    </div>
  );
}

/**
 * Inline per-clinic credit limit (My Hub, 9/4/26) - the figure the rep sees next to the clinic's
 * outstanding balance on My Hub. Same shape as RateCell: a blank box saves as null ("no limit
 * set"), never 0, and Save only appears once the value actually changed.
 */
export function CreditLimitCell({ clinic, onSaved }) {
  const t = useT();
  const [value, setValue] = useState(clinic.creditLimit ?? '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setValue(clinic.creditLimit ?? '');
  }, [clinic.creditLimit]);

  const dirty = String(value) !== String(clinic.creditLimit ?? '');

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const trimmed = String(value).trim();
      const limit = trimmed === '' ? null : Number(trimmed);
      const res = await clinicService.setCreditLimit(clinic._id, limit);
      const applied = res.data?.data?.creditLimit ?? res.data?.creditLimit ?? limit;
      onSaved(clinic._id, applied);
      setSaved(true);
    } catch (err) {
      alert(t('Failed to save credit limit'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[11px] text-[#97a3b6]">$</span>
      <input
        type="number"
        min="0"
        step="0.01"
        value={value}
        onChange={(e) => { setValue(e.target.value); setSaved(false); }}
        aria-label={t('Credit limit for {name}', { name: clinic.name })}
        placeholder="—"
        className="w-[84px] h-[30px] border border-[#d6dce8] rounded-[6px] px-2 text-[12px] text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff]"
      />
      {dirty && (
        <button
          onClick={save}
          disabled={saving}
          className="h-[30px] px-2 bg-[#0089ff] text-white text-[11px] font-medium rounded-[6px] hover:bg-[#0077e6] disabled:opacity-50"
        >
          {saving ? '...' : t('Save')}
        </button>
      )}
      {saved && !dirty && <span className="material-symbols-outlined text-[16px] text-[#007a55]">check</span>}
    </div>
  );
}

export function NewClinicUser({ clinics, onCreated }) {
  const t = useT();
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', password: '', clinic: '' });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const canCreate = form.firstName && form.lastName && form.email && form.password && form.clinic && !saving;

  const create = async () => {
    setSaving(true);
    setMessage('');
    try {
      await userService.createUser({ ...form, role: 'clinic' });
      setMessage(t('Clinic user {email} created.', { email: form.email }));
      setForm({ firstName: '', lastName: '', email: '', password: '', clinic: '' });
      onCreated?.(); // new clinic logins start as OWNER; refresh the role manager so it lists them
    } catch (err) {
      alert(t('Failed to create clinic user'));
    } finally {
      setSaving(false);
    }
  };

  const input = 'h-[40px] border border-[#d6dce8] rounded-[8px] px-3 text-[13px] text-[#0f172a]';
  return (
    <div className="bg-white border border-[#e2e8f0] rounded-[14px] shadow-sm p-5 mb-6">
      <p className="text-[14px] font-semibold text-[#0f172a] mb-1">{t('New Clinic User')}</p>
      <p className="text-[12px] text-[#64748b] mb-3">
        {t('A clinic login sees and submits ONLY for its own clinic. Bound to exactly one clinic.')}
      </p>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-2 mb-3">
        <input className={input} placeholder={t('First name')} value={form.firstName} onChange={set('firstName')} />
        <input className={input} placeholder={t('Last name')} value={form.lastName} onChange={set('lastName')} />
        <input className={input} placeholder={t('Email')} value={form.email} onChange={set('email')} />
        <input className={input} type="password" placeholder={t('Password')} value={form.password} onChange={set('password')} />
        <select className={input + ' bg-white'} aria-label={t('Clinic for user')} value={form.clinic} onChange={set('clinic')}>
          <option value="">{t('Clinic...')}</option>
          {clinics.map((c) => (
            <option key={c._id} value={c._id}>{c.name}</option>
          ))}
        </select>
      </div>
      <button
        onClick={create}
        disabled={!canCreate}
        className={`h-[36px] px-5 rounded-[6px] text-[13px] font-medium transition-colors ${
          canCreate ? 'bg-[#0f172a] text-white hover:bg-[#1e293b]' : 'bg-[#e2e8f0] text-[#97a3b6] cursor-not-allowed'
        }`}
      >
        {saving ? t('Creating...') : t('Create Clinic User')}
      </button>
      {message && <p className="text-[12px] text-[#007a55] mt-2">{message}</p>}
    </div>
  );
}

/**
 * Phase 8: set a clinic login's practice role. OWNER (default) sees the whole practice's records
 * and invoices; USER sees only their own submissions and no invoices. Reversible - switch back to
 * Owner anytime. The server enforces owner|user AND clinic-users-only; this is the cosmetic surface.
 */
export function ClinicUserRoles({ refreshKey }) {
  const t = useT();
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState('');
  const [role, setRole] = useState('owner');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    // limit:100 (PaginationDto max) - the default of 20 would silently drop the OLDEST clinic logins,
    // the established accounts most likely to need restricting. A searchable picker is
    // the proper fix before a practice exceeds 100 logins.
    userService.getUsers({ role: 'clinic', limit: 100 }).then((res) => {
      setUsers(unwrap(res).filter((u) => u.role === 'clinic'));
    }).catch(() => setUsers([]));
  }, [refreshKey]);

  useEffect(() => {
    const u = users.find((x) => x._id === selected);
    // Match the SERVER's enforcement, not `=== 'user'` - a stored 'User' must show as User, not the
    // fail-open "Owner". Shared mirror in utils/clinicRole.
    setRole(clinicRoleSelectValue(u?.clinicRole));
    setMessage('');
  }, [selected, users]);

  const save = async () => {
    setSaving(true);
    setMessage('');
    try {
      await userService.setClinicRole(selected, role);
      setUsers((prev) => prev.map((u) => (u._id === selected ? { ...u, clinicRole: role } : u)));
      setMessage(t('Saved.'));
    } catch (err) {
      alert(t('Failed to save role'));
    } finally {
      setSaving(false);
    }
  };

  const select = 'h-[40px] border border-[#d6dce8] rounded-[8px] px-2 text-[13px] text-[#0f172a] bg-white';
  return (
    <div className="bg-white border border-[#e2e8f0] rounded-[14px] shadow-sm p-5 mb-6">
      <p className="text-[14px] font-semibold text-[#0f172a] mb-1">{t('Clinic Login Roles')}</p>
      <p className="text-[12px] text-[#64748b] mb-3">
        <strong>{t('Owner')}</strong> {t("sees the whole practice's records and invoices.")}{' '}
        <strong>{t('User')}</strong> {t('sees only their own submissions and no invoices. New logins start as Owner; switch back anytime.')}
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          aria-label={t('Clinic login')}
          className={`${select} w-full max-w-[320px]`}
        >
          <option value="">{t('Select a clinic login...')}</option>
          {users.map((u) => (
            <option key={u._id} value={u._id}>
              {(`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email) + (u.email ? ` (${u.email})` : '')}
            </option>
          ))}
        </select>
        {selected && (
          <>
            <select value={role} onChange={(e) => setRole(e.target.value)} aria-label={t('Practice role')} className={`${select} w-[190px]`}>
              <option value="owner">{t('Owner (full practice)')}</option>
              <option value="user">{t('User (own only)')}</option>
            </select>
            <button
              onClick={save}
              disabled={saving}
              className="h-[40px] px-5 bg-[#0089ff] text-white text-[13px] font-medium rounded-[8px] hover:bg-[#0077e6] transition-colors disabled:opacity-50"
            >
              {saving ? t('Saving...') : t('Save Role')}
            </button>
          </>
        )}
        {message && <span className="text-[12px] text-[#007a55]">{message}</span>}
      </div>
      {users.length === 0 && (
        <p className="text-[12px] text-[#64748b] mt-2">{t('No clinic logins yet - create one below.')}</p>
      )}
    </div>
  );
}
